import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { usePermissions } from '../context/PermissionsContext';

interface Note {
  id?: number;
  client_id: number;
  title: string;
  content: string;
  created_by: number;
  created_at?: string;
  updated_at?: string;
  created_by_name?: string;
  created_by_surname?: string;
}

interface ClientPersonalNotesTabProps {
  clientId: string;
  auditLogs?: any[]; // ✅ ADD THIS
  onRefreshAuditLogs?: () => void; // ✅ ADD THIS
}

const API_URL = process.env.REACT_APP_API_URL || '';

const ClientPersonalNotesTab: React.FC<ClientPersonalNotesTabProps> = ({ clientId, auditLogs = [], onRefreshAuditLogs }) => {
  const { can } = usePermissions();
  const [notes, setNotes] = useState<Note[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // New note form state
  const [newNote, setNewNote] = useState({
    title: '',
    content: ''
  });

  // Fetch existing notes
  useEffect(() => {
    fetchNotes();
  }, [clientId]);

  const fetchNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/client-notes?clientId=${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setNotes(response.data);
    } catch (error: any) {
      console.error('Error fetching notes:', error);
      setError('Failed to load notes');
    }
  };

  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newNote.title.trim() || !newNote.content.trim()) {
      setError('Title and content are required');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.post(
        `${API_URL}/api/client-notes`,
        {
          clientId: parseInt(clientId),
          title: newNote.title.trim(),
          content: newNote.content.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Note created successfully!');
      setNewNote({ title: '', content: '' });
      setIsCreateModalOpen(false);
      fetchNotes(); // Refresh the list
      
    } catch (error: any) {
      console.error('Error creating note:', error);
      setError(error.response?.data?.message || 'Failed to create note');
    } finally {
      setIsLoading(false);
    }
  };

  

  const openCreateModal = () => {
    setNewNote({ title: '', content: '' });
    setError('');
    setSuccess('');
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewNote({ title: '', content: '' });
    setError('');
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ✅ ADD THESE FUNCTIONS HERE
  const getFilteredNotes = () => {
    if (!searchTerm.trim()) return notes;
    
    const term = searchTerm.toLowerCase().trim();
    return notes.filter(note => 
      note.title.toLowerCase().includes(term) ||
      note.content.toLowerCase().includes(term)
    );
  };

  const getFilteredAuditLogs = () => {
    if (!searchTerm.trim()) return auditLogs || [];
    
    const term = searchTerm.toLowerCase().trim();
    return (auditLogs || []).filter(log => 
      log.field_name?.toLowerCase().includes(term) ||
      log.old_value?.toLowerCase().includes(term) ||
      log.new_value?.toLowerCase().includes(term) ||
      log.changed_by_name?.toLowerCase().includes(term) ||
      log.note?.toLowerCase().includes(term)
    );
  };

  const filteredNotes = getFilteredNotes();
  const filteredAuditLogs = getFilteredAuditLogs();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Client Notes</h3>
        <div className="text-sm text-gray-500">
          Client ID: {clientId}
        </div>
      </div>

            {/* Create New Note Button and Search */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div className="flex flex-wrap gap-2">
                {can('notes', 'write') && (
                  <button
                    onClick={openCreateModal}
                    className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Create New Note
                  </button>
                )}
              </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search notes & audit logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <svg
              className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
          <div className="text-sm text-gray-500 whitespace-nowrap">
            {searchTerm ? (
              <span>
                Found {filteredNotes.length + filteredAuditLogs.length} result(s)
              </span>
            ) : (
              <span>
                {notes.length} note{notes.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

                  {/* Notes List */}
      <div className="space-y-4">
        {filteredNotes.length === 0 && !searchTerm && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500">No notes created yet.</p>
            <p className="text-sm text-gray-400 mt-1">Click "Create New Note" to add your first note.</p>
          </div>
        )}
        {filteredNotes.length === 0 && searchTerm && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500">No notes match your search.</p>
          </div>
        )}
        {filteredNotes.map((note) => (
          <div
            key={note.id}
            className="bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start mb-3">
  <h4 className="text-lg font-semibold text-gray-900">{note.title}</h4>
</div>
            
            <div className="prose prose-sm max-w-none mb-4">
              <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
            </div>
            
            <div className="text-xs text-gray-500 border-t pt-2 flex justify-between items-center">
  <div>
    <span>Created {formatDate(note.created_at!)}</span>
    {note.updated_at && note.updated_at !== note.created_at && (
      <span> • Updated {formatDate(note.updated_at)}</span>
    )}
  </div>
  <div>
    <span className="font-medium">Created by:</span>
    <span className="ml-1">
      {note.created_by_surname 
        ? `${note.created_by_name} ${note.created_by_surname}` 
        : note.created_by_name || 'Unknown User'}
    </span>
  </div>
</div>

          </div>
        ))}
      </div>

            {/* Audit Logs Section */}
      <div className="mt-8 border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-semibold">Audit Logs</h4>
          <button
            onClick={() => setShowAuditLogs(!showAuditLogs)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showAuditLogs ? 'Hide Audit Logs' : 'Show Audit Logs'}
          </button>
        </div>

        {showAuditLogs && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredAuditLogs.length === 0 && searchTerm && (
              <p className="text-gray-500 text-sm">No audit logs match your search.</p>
            )}
            {filteredAuditLogs.length === 0 && !searchTerm && (
              <p className="text-gray-500 text-sm">No changes recorded yet</p>
            )}
            {filteredAuditLogs.map((log) => (
              <div key={log.id} className="bg-gray-50 p-3 rounded border border-gray-200">
                <div className="text-xs font-semibold text-gray-600">System Generated Note:</div>
                <div className="text-xs text-gray-400 mb-2">{log.note || 'No additional notes'}</div>
                
                <div className="flex justify-between items-start border-t border-gray-200 pt-2">
                  <div>
                    <span className="font-medium text-sm">{log.field_name.replace(/_/g, ' ')}</span>
                  </div>
                </div>

                <div className="text-sm mt-1">
                  <span className="text-red-600 line-through mr-2">
                    {log.old_value || '(empty)'}
                  </span>
                  <span className="text-green-600">→ {log.new_value || '(empty)'}</span>
                </div>

                <div className="text-xs text-gray-500 mt-2 border-t border-gray-200 pt-1 flex justify-between">
                  <span>
                    <span className="font-medium">Changed by:</span>
                    <span className="ml-1">
                      {log.changed_by_surname 
                        ? `${log.changed_by_name} ${log.changed_by_surname}` 
                        : log.changed_by_name || 'Unknown User'}
                    </span>
                  </span>
                  <span className="text-gray-400">
                    {new Date(log.changed_at).toLocaleString('en-ZA')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Note Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Create New Note</h3>
            </div>
            
            <form onSubmit={handleCreateNote} className="p-6 space-y-4">
              {/* Title Input */}
              <div>
                <label htmlFor="noteTitle" className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  id="noteTitle"
                  value={newNote.title}
                  onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter note title"
                  required
                />
              </div>

              {/* Content Textarea */}
              <div>
                <label htmlFor="noteContent" className="block text-sm font-medium text-gray-700 mb-1">
                  Content *
                </label>
                <textarea
                  id="noteContent"
                  value={newNote.content}
                  onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                  placeholder="Enter your note content..."
                  required
                />
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? 'Creating...' : 'Create Note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPersonalNotesTab;